<template>
  <div class="esign-document-viewer">
    <!-- Loading state -->
    <div v-if="loading" class="flex flex-center q-pa-xl">
      <q-spinner-dots size="48px" color="primary" />
      <div class="q-ml-md text-grey-7">Loading document pages...</div>
    </div>

    <!-- No pages available -->
    <div v-else-if="!hasPages" class="flex flex-center q-pa-xl">
      <q-icon name="description" size="48px" color="grey-5" />
      <div class="q-ml-md text-grey-6">Document pages are not available yet.</div>
    </div>

    <!-- Scrollable PDF pages -->
    <div v-else ref="scrollContainer" class="pdf-scroll-area">
      <q-virtual-scroll
        ref="virtualScroll"
        class="viewer-virtual-scroll"
        :items="virtualItems"
        :virtual-scroll-item-size="estimatedItemHeight"
        :virtual-scroll-slice-size="5"
        :virtual-scroll-slice-ratio-before="1"
        :virtual-scroll-slice-ratio-after="1"
        :scroll-target="scrollTargetEl"
        v-slot="{ item }"
        @virtual-scroll="onVirtualScroll"
      >
        <!-- Document header item -->
        <div v-if="item.type === 'header'" :key="'h-' + item.doc.id" class="document-header">
          <q-icon name="picture_as_pdf" size="20px" color="red-7" class="q-mr-xs" />
          <span class="text-subtitle2">{{ item.doc.name }}</span>
          <slot name="header-badge" :document="item.doc" />
        </div>

        <!-- Page item -->
        <div
          v-else
          :key="'p-' + item.doc.id + '-' + item.page.pageNumber"
          class="page-wrapper"
        >
          <div
            class="page-container"
            :style="pageContainerStyle(item.page)"
            :data-doc-id="item.doc.id"
            :data-page="item.page.pageNumber"
            :data-width-pt="item.page.widthPt"
            :data-height-pt="item.page.heightPt"
          >
            <q-img
              :src="item.page.imageUrl"
              :ratio="item.page.widthPt / item.page.heightPt"
              fit="contain"
              loading="lazy"
              no-spinner
              class="page-image"
            />

            <!-- Consumer-provided field/annotation overlays -->
            <slot
              name="page-fields"
              :document="item.doc"
              :page="item.page"
              :containerWidth="scaledContainerWidth"
              :scale="activeScale"
            />
          </div>

          <div class="page-number-label text-caption text-grey-6">
            Page {{ item.page.pageNumber }}
          </div>
        </div>
      </q-virtual-scroll>
    </div>
  </div>
</template>

<script>
import { useDragScroll } from './pdf/useDragScroll.js';
import { useScrollZoom } from './pdf/useScrollZoom.js';

export default {
  name: 'ESignDocumentViewer',
  props: {
    /** Array of documents: { id, name, pageCount } */
    documents: { type: Array, required: true },
    /**
     * Map of documentId -> array of page data.
     * Each page: { pageNumber, imageUrl, widthPt, heightPt }
     */
    pagesByDocument: { type: Object, default: () => ({}) },
    /** Whether pages are still loading */
    loading: { type: Boolean, default: false },
    /** Base page width in pixels before zoom (default 800) */
    baseWidth: { type: Number, default: 800 },
    /** Show document name headers between documents */
    showHeaders: { type: Boolean, default: true },
    /**
     * Callback passed to useDragScroll's isInteractive option.
     * Return true for elements that should NOT trigger drag-scroll.
     */
    isInteractive: { type: Function, default: null },
    /**
     * v-model:scale - external scale control.
     * When provided, overrides internal scale and emits updates.
     */
    scale: { type: Number, default: null },
  },
  emits: ['update:scale'],
  data() {
    return {
      internalScale: 1,
      scrollTargetEl: undefined,
    };
  },
  computed: {
    /** Resolved scale: uses v-model:scale if provided, otherwise internal. */
    activeScale() {
      return this.scale != null ? this.scale : this.internalScale;
    },
    hasPages() {
      return Object.keys(this.pagesByDocument).length > 0 &&
        Object.values(this.pagesByDocument).some(pages => pages.length > 0);
    },
    /** Flat list of virtual items: doc headers interleaved with pages. */
    virtualItems() {
      const items = [];
      for (const doc of this.documents) {
        if (this.showHeaders) {
          items.push({ type: 'header', doc });
        }
        const pages = this.getDocumentPages(doc.id);
        for (const page of pages) {
          items.push({ type: 'page', doc, page });
        }
      }
      return items;
    },
    scaledContainerWidth() {
      return Math.round(this.baseWidth * this.activeScale);
    },
    estimatedItemHeight() {
      const firstPage = this._findFirstPage();
      if (firstPage) {
        const scaledWidth = this.scaledContainerWidth;
        const pageHeight = Math.round(scaledWidth * (firstPage.heightPt / firstPage.widthPt));
        return pageHeight + 24 + 16;
      }
      return Math.round(1000 * this.activeScale) + 40;
    },
  },
  watch: {
    hasPages(val) {
      if (val) this.$nextTick(() => {
        this.attachScrollHandlers();
        this.fitToContainer();
      });
    },
    activeScale() {
      this.$nextTick(() => {
        this.$refs.virtualScroll?.reset();
      });
    },
  },
  mounted() {
    if (this.hasPages) {
      this.attachScrollHandlers();
      this.$nextTick(() => this.fitToContainer());
    }
  },
  beforeUnmount() {
    this._dragScroll?.detach();
    this._scrollZoom?.detach();
    clearTimeout(this._scrollFallbackTimer);
    this.$refs.scrollContainer?.removeEventListener('scroll', this._scrollHandler);
  },
  methods: {
    /** Set the active scale, respecting v-model:scale when provided. */
    setScale(z) {
      if (this.scale != null) {
        this.$emit('update:scale', z);
      } else {
        this.internalScale = z;
      }
    },

    /**
     * Calculates and applies a scale that fits the page width to the container.
     * Capped at 1.0 - never upscales beyond 100%.
     */
    fitToContainer() {
      const container = this.$refs.scrollContainer;
      if (!container) return;
      const availableWidth = container.clientWidth - 32; // padding
      const baseW = this.baseWidth || 800;
      const fitScale = Math.max(0.25, Math.min(1, +(availableWidth / baseW).toFixed(2)));
      this.setScale(fitScale);
    },

    attachScrollHandlers() {
      const el = this.$refs.scrollContainer;
      if (!el || this._dragScroll) return;

      this.scrollTargetEl = el;

      this._dragScroll = useDragScroll({
        isInteractive: this.isInteractive || ((e) =>
          e.target.closest('.signing-field-overlay.pending') != null ||
          e.target.closest('.sig-interactive') != null ||
          e.target.closest('.resize-handle') != null ||
          e.target.tagName === 'CANVAS'),
      });
      this._dragScroll.attach(el);

      this._scrollZoom = useScrollZoom({
        getZoom: () => this.activeScale,
        setZoom: (z) => this.setScale(z),
        minZoom: 0.25,
        maxZoom: 3,
      });
      this._scrollZoom.attach(el);

      // Virtual scroll fallback: detect when rendered range hasn't caught up
      this._renderedFrom = 0;
      this._renderedTo = 0;
      this._scrollFallbackTimer = null;
      this._scrollHandler = () => {
        clearTimeout(this._scrollFallbackTimer);
        this._scrollFallbackTimer = setTimeout(() => this._checkRenderedPages(), 150);
      };
      el.addEventListener('scroll', this._scrollHandler, { passive: true });
    },

    /** Track virtual scroll's rendered range via its event. */
    onVirtualScroll({ from, to }) {
      this._renderedFrom = from;
      this._renderedTo = to;
    },

    /**
     * Fallback for fast scrolling: if the expected center-page index falls
     * outside the virtual scroll's rendered range, force it to catch up.
     */
    _checkRenderedPages() {
      const container = this.$refs.scrollContainer;
      const vs = this.$refs.virtualScroll;
      if (!container || !vs || this.virtualItems.length === 0) return;

      const scrollTop = container.scrollTop;
      const clientHeight = container.clientHeight;
      const itemHeight = this.estimatedItemHeight;

      const centerY = scrollTop + clientHeight / 2;
      const expectedIndex = Math.min(
        this.virtualItems.length - 1,
        Math.max(0, Math.floor(centerY / itemHeight))
      );

      if (expectedIndex < this._renderedFrom || expectedIndex > this._renderedTo) {
        vs.scrollTo(expectedIndex, 'center-force');
      }
    },

    _findFirstPage() {
      for (const doc of this.documents) {
        const pages = this.getDocumentPages(doc.id);
        if (pages.length > 0) return pages[0];
      }
      return null;
    },

    getDocumentPages(docId) {
      return this.pagesByDocument[docId] || [];
    },

    pageContainerStyle(page) {
      const scaledWidth = this.scaledContainerWidth;
      return {
        width: scaledWidth + 'px',
        maxWidth: scaledWidth + 'px',
        aspectRatio: `${page.widthPt} / ${page.heightPt}`,
      };
    },

    /** Scroll to a specific page in a document */
    scrollToPage(documentId, pageNumber) {
      const vs = this.$refs.virtualScroll;
      if (!vs) return;
      const index = this.virtualItems.findIndex(
        item => item.type === 'page' &&
          item.doc.id === documentId &&
          item.page.pageNumber === pageNumber
      );
      if (index >= 0) {
        vs.scrollTo(index, 'center-force');
      }
    },

    /**
     * Scroll to a specific annotation field within a document page.
     * First scrolls to the page via virtual scroll, then fine-tunes
     * the scroll offset to center the field vertically.
     * @param {string} documentId
     * @param {number} pageNumber
     * @param {number} fieldYPercent - field Y position as percentage of page height
     * @returns {Promise} resolves after scroll settles
     */
    scrollToField(documentId, pageNumber, fieldYPercent) {
      const container = this.$refs.scrollContainer;
      if (!container) return Promise.resolve();

      const pageEl = container.querySelector(
        `[data-doc-id="${documentId}"][data-page="${pageNumber}"]`
      );

      // If the page is already rendered, check whether the field is already visible
      if (pageEl) {
        const pageRect = pageEl.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const fieldViewportY = pageRect.top + (pageRect.height * fieldYPercent / 100);
        const margin = 60; // px buffer so fields near edges still count as visible
        if (fieldViewportY >= containerRect.top + margin && fieldViewportY <= containerRect.bottom - margin) {
          return Promise.resolve(); // already in view - no scroll needed
        }
      }

      this.scrollToPage(documentId, pageNumber);
      return new Promise(resolve => {
        setTimeout(() => {
          const pageElAfter = container.querySelector(
            `[data-doc-id="${documentId}"][data-page="${pageNumber}"]`
          );
          if (!pageElAfter) { resolve(); return; }
          const pageRect = pageElAfter.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const fieldAbsY = pageRect.top - containerRect.top + container.scrollTop
            + (pageRect.height * fieldYPercent / 100);
          const targetScroll = fieldAbsY - container.clientHeight / 3;
          container.scrollTo({ top: targetScroll, behavior: 'smooth' });
          setTimeout(resolve, 350);
        }, 200);
      });
    },

    /** Returns the scroll container element for external composable attachment */
    getScrollContainer() {
      return this.$refs.scrollContainer;
    },
  },
};
</script>

<style scoped>
.esign-document-viewer {
  width: 100%;
  height: 100%;
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.pdf-scroll-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: auto;
  padding: 16px;
  background: #e8e8e8;
}

.viewer-virtual-scroll {
  width: max-content;
  margin-left: auto;
  margin-right: auto;
}

.document-header {
  display: flex;
  align-items: center;
  padding: 8px 12px 16px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.page-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 16px;
}

.page-container {
  position: relative;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.page-image {
  width: 100%;
  display: block;
}

.page-number-label {
  padding-top: 4px;
  text-align: center;
}
</style>
